import { useCallback, useEffect, useState } from "react";
import { Loader2, Mail, Plus, Send, Trash2, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

// Staff panel — used INLINE on the campsite detail page for primary
// owners (not staff, not admin). Wraps the mobile app's:
//   GET     /campsites/{id}/staff       — list current staff + pending invites
//   POST    /campsites/{id}/staff/invite   { email } — create invite
//   DELETE  /campsites/{id}/staff/{user_id} — remove staff
//
// Non-primary-owner viewers get the same UI in read-only mode (backend will
// 403 the mutating endpoints anyway, but we hide the affordances here to
// keep the UX honest).
export default function StaffPanel({ campsite, isPrimaryOwner }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get(`/campsites/${campsite.campsite_id}/staff`);
      setData(data);
    } catch (e) {
      // 403 means the viewer isn't the primary owner — hide the whole panel
      // silently in that case (parent already knows). Any other error we
      // surface so a real problem doesn't stay invisible.
      if (e?.response?.status !== 403) {
        setError(e?.response?.data?.detail || "Could not load staff.");
      }
      setData({ staff: [], invites: [] });
    }
  }, [api, campsite.campsite_id]);

  useEffect(() => { if (isPrimaryOwner) load(); }, [isPrimaryOwner, load]);

  const invite = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/campsites/${campsite.campsite_id}/staff/invite`, { email: email.trim().toLowerCase() });
      if (r.already_staff)   toast.info("That user is already on the staff list.");
      else if (r.already_invited) toast.info("An invite is already pending for that email.");
      else                    toast.success("Invite sent.");
      setEmail("");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not send invite.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const removeStaff = async (uid) => {
    if (!window.confirm("Remove this staff member?")) return;
    try {
      await api.delete(`/campsites/${campsite.campsite_id}/staff/${uid}`);
      toast.success("Removed.");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not remove staff.";
      toast.error(detail);
    }
  };

  if (!isPrimaryOwner) return null;
  if (!data) {
    return (
      <section className="card-elev p-6 flex items-center gap-3" style={{ color: "var(--on-surface-muted)" }} data-testid="staff-panel-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading staff…
      </section>
    );
  }

  return (
    <section className="card-elev p-5 flex flex-col gap-4" data-testid="staff-panel">
      <div className="flex items-center gap-2">
        <Users className="w-4 h-4" style={{ color: "var(--brand)" }} />
        <h2 className="text-lg font-extrabold" style={{ color: "var(--on-surface)" }}>Team</h2>
      </div>

      {error && <div className="text-sm" style={{ color: "var(--premium)" }}>{error}</div>}

      {/* Invite form */}
      <form onSubmit={invite} className="flex flex-col sm:flex-row gap-2" data-testid="staff-invite-form">
        <div className="relative flex-1">
          <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@example.com"
            className="w-full pl-10 pr-3 py-2.5 rounded-xl border text-sm"
            style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
            data-testid="staff-invite-email"
          />
        </div>
        <button type="submit" disabled={busy} className="btn btn-green justify-center" data-testid="staff-invite-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4" /> Invite</>}
        </button>
      </form>

      {/* Existing staff */}
      {(data.staff || []).length > 0 && (
        <div>
          <div className="text-[10px] font-extrabold uppercase tracking-widest mb-1" style={{ color: "var(--on-surface-muted)" }}>
            Current team
          </div>
          <div className="flex flex-col divide-y" style={{ borderColor: "var(--border-c)" }}>
            {data.staff.map((s) => (
              <div key={s.user_id} className="py-2 flex items-center gap-3" data-testid={`staff-row-${s.user_id}`}>
                {s.avatar_data ? <img src={s.avatar_data} alt="" className="w-8 h-8 rounded-full object-cover" />
                  : <div className="w-8 h-8 rounded-full flex items-center justify-center font-extrabold text-xs"
                         style={{ background: "var(--brand)", color: "white" }}>{(s.name?.[0] || "?").toUpperCase()}</div>}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate" style={{ color: "var(--on-surface)" }}>{s.name || s.nickname || "—"}</div>
                  <div className="text-xs truncate" style={{ color: "var(--on-surface-muted)" }}>{s.email}</div>
                </div>
                <button onClick={() => removeStaff(s.user_id)} className="p-2 rounded-lg hover:bg-black/5"
                        title="Remove" data-testid={`staff-remove-${s.user_id}`}>
                  <Trash2 className="w-4 h-4 opacity-60" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending invites */}
      {(data.invites || []).length > 0 && (
        <div>
          <div className="text-[10px] font-extrabold uppercase tracking-widest mb-1" style={{ color: "var(--on-surface-muted)" }}>
            Pending invites
          </div>
          <div className="flex flex-col divide-y" style={{ borderColor: "var(--border-c)" }}>
            {data.invites.map((i) => (
              <div key={i.invite_code} className="py-2 flex items-center gap-3" data-testid={`staff-invite-${i.invite_code}`}>
                <Send className="w-4 h-4 opacity-60" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate" style={{ color: "var(--on-surface)" }}>{i.email}</div>
                  <div className="text-xs" style={{ color: "var(--on-surface-muted)" }}>
                    invited {(i.invited_at || "").slice(0, 10)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(!data.staff?.length && !data.invites?.length) && (
        <div className="text-sm" style={{ color: "var(--on-surface-muted)" }} data-testid="staff-empty">
          No team members yet. Invite one above.
        </div>
      )}
    </section>
  );
}
